import type { CompensationFilters } from '@revelio/data-access';

import { type Filter, FilterId, FilterType } from '../types/filters';
import { getCustomRoleFilter } from './getCustomRoleFilter';
import { getDateFromFilters } from './getDateFilter';
import { getListFilter } from './getListFilter';
import { getStaticListBoolean } from './getStaticListBoolean';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

export const getTopNFilters = (
  effectiveFilters: Filter[]
): CompensationFilters => {
  const baseFilters = effectiveFilters.reduce<CompensationFilters>(
    (acc, filter): CompensationFilters => {
      const getTopNListFilter = (key: keyof CompensationFilters) => {
        if (filter.type !== FilterType.LIST) return acc;
        return {
          ...acc,
          ...getListFilter<CompensationFilters>(filter, key),
        };
      };

      return ((): CompensationFilters => {
        switch (filter.id) {
          case FilterId.COMPANY:
            return { ...acc, ...getV2CompanyFilters(filter) };

          case FilterId.GEOGRAPHY:
            return { ...acc, ...getV2GeographyFilters(filter) };

          case FilterId.ROLE:
            return { ...acc, ...getCustomRoleFilter(filter, effectiveFilters) };

          case FilterId.ROLE_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const customRoleAcc = acc.custom_role || {};
              return {
                ...acc,
                custom_role: {
                  ...customRoleAcc,
                  taxonomyId: stripVersionFromTaxonomyId(filter.value),
                },
              };
            }
            return acc;
          }

          case FilterId.CONTINGENT_WORKERS:
            return {
              ...acc,
              ...getStaticListBoolean(filter, 'contingent_workers'),
            };

          case FilterId.ETHNICITY:
            return getTopNListFilter('ethnicity');

          case FilterId.GENDER:
            return getTopNListFilter('gender');

          case FilterId.EDUCATION:
            return getTopNListFilter('highest_degree');

          case FilterId.SENIORITY:
            return getTopNListFilter('seniority');

          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.DATE_RANGE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.SNAPSHOT_DATE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.TITLE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.KEYWORDS:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.DATA_ADJUSTMENTS:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.POSTINGS_SOURCE:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.OVERALL_RATING:
          // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
          case FilterId.SKILL:
          default:
            return acc;
        }
      })();
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters: effectiveFilters,
    granularity: 'month',
  });

  return { ...baseFilters, ...dateFilter };
};
